import type {
	PointClickEventObject,
	SeriesMapDataOptions,
	Point,
	ChartOptions,
} from 'highcharts';
import ScriptInject from '../../utils/script-inject';
import Component from '../../Component';
import {
	usTopologyData,
	HighchartsTopologyData,
	getStatesProperties,
	StateWithBills,
	getStatesWithBills,
	BillData,
} from './source';
import HighchartsMapPinchZoom from './map-zooming';
import { mobileAndTablet } from '../../match-media';

/* eslint-disable camelcase */

interface SeriesItemData extends SeriesMapDataOptions {
	code: string;
	localityCode: string;
	name: string;
	borderColor: string;
}

export interface BillTrackerMapState {
	visible: boolean;
	billFilter: (bill: BillData) => boolean;
}

const MIN_COUNT_TO_COLOR = {
	20: '#003A48',
	15: '#005E67',
	10: '#78B7BE',
	5: '#BACED2',
	1: '#D9E2E4',
	0: '#F3F3F7',
};

const MIN_COUNT_TO_COLOR_KEYS_REVERSED = Object.keys(MIN_COUNT_TO_COLOR).map(
	(key) => Number(key)
);
MIN_COUNT_TO_COLOR_KEYS_REVERSED.reverse();

class BillTrackerMap extends Component<HTMLElement> {
	static selector = '.bill-tracker__map';

	usTopologyData?: HighchartsTopologyData;
	statesWithBills?: StateWithBills[] = [];
	seriesData?: SeriesItemData[];
	chart?: typeof window.Highcharts;

	tooltipContainer?: HTMLElement;
	tooltipSlot?: HTMLElement;
	tooltipClose?: HTMLButtonElement;
	footer?: HTMLElement;

	state: BillTrackerMapState = {
		visible: true,
		billFilter: () => true,
	};

	static children = {
		tooltipContainer: '.bill-tracker__tooltip-container',
		tooltipSlot: '.bill-tracker__tooltip-slot',
		tooltipClose: '.bill-tracker__tooltip-close',
		footer: '.bill-tracker__footer',
	};

	async afterInit() {
		this.footer = this.element.parentElement?.querySelector(
			BillTrackerMap.children.footer
		) as HTMLElement;

		const [usTopology, statesWithBills, seriesData] = await Promise.all([
			usTopologyData(),
			getStatesWithBills(),
			getStatesProperties(),
			ScriptInject.inject(['proj4', 'highmaps']).then(() =>
				ScriptInject.inject([
					'highcharts-module-export',
					'highmaps-module-export',
					'highmaps-module-offline-export',
					'highmaps-mapdata-us',
					'highcharts-module-accessibility',
				])
			),
		]);

		this.usTopologyData = usTopology;
		this.statesWithBills = statesWithBills;
		this.seriesData = seriesData.map((stateProperties) => ({
			code: stateProperties['hc-a2'],
			localityCode: stateProperties['hc-key'],
			name: stateProperties.name,
			color: this.getStateColor(stateProperties['hc-key']),
		}));

		this.initializeMap();
		this.initializeTooltip();
		this.handleBreakpoints();
	}

	initializeMap() {
		// Allow custom attributes
		window.Highcharts.AST.allowedAttributes.push('data-status');
		window.Highcharts.AST.allowedAttributes.push('data-category-group');

		this.chart = window.Highcharts.mapChart(<ChartOptions>{
			chart: {
				animation: false,
				map: this.usTopologyData,
				margin: [0, 20, 0, 20],
				borderWidth: 0,
				renderTo: this.element,
				events: {
					render: this.onChartRender.bind(this),
					load: this.onChartLoad.bind(this),
				},
				style: {
					fontFamily: 'Manrope',
				},
			},
			title: {
				text: '',
			},
			credits: {
				enabled: false,
			},

			plotOptions: {
				series: {
					states: {
						hover: {
							enabled: false,
						},
					},
					animation: false,
					clip: false,
				},
			},

			exporting: {
				filename: 'Bill Tracker Map',
				buttons: {
					contextButton: {
						enabled: false,
					},
				},
				chartOptions: {
					title: {
						text: 'Bill Tracker Map',
					},
					chart: {
						width: 1200,
						height: 840,
						style: {
							fontFamily: 'Arial',
						},
					},

					plotOptions: {
						borderWidth: 0.25,
					},
				},
			},

			legend: {
				layout: 'horizontal',
				width: 268,
				squareSymbol: false,
				symbolWidth: 268,
				symbolHeight: 20,
				symbolRadius: 10,
				labelFormat: '',
				title: {
					text: 'Key: The darker the state, the more bills are being considered.',
					style: {
						width: 268,
						top: 50,
					},
				},
			},

			colorAxis: {
				minColor: '#D9E2E4',
				maxColor: '#003A48',
			},

			mapNavigation: {
				enabled: true,
				enableMouseWheelZoom: false,
			},

			tooltip: {
				useHTML: true,
				enabled: false,
			},

			series: [
				{
					animation: {
						duration: 0,
					},
					data: this.seriesData?.map(
						this.updateSeriesItemData.bind(this)
					),
					events: {
						click: ({
							point,
						}: PointClickEventObject & {
							point: Point & SeriesItemData;
						}) => {
							this.showTooltip(point.localityCode);
						},
					},
					borderColor: '#003A48',
					borderWidth: 0.25,
					joinBy: ['postal-code', 'code'],
					dataLabels: {
						enabled: true,
						color: '#FFFFFF',
						formatter() {
							const { point } = this;
							return point.code;
						},
						overflow: true,
						crop: false,
						allowOverlap: false,
						softConnector: true,
					},
					name: 'Population density',
					tooltip: {
						followPointer: false,
						distance: 0,
						stickOnContact: true,
						animation: false,
						useHTML: true,
						headerFormat: '',
						outside: true,
						pointFormatter: <() => string>function () {
							const parentComponent =
								this.series.chart.parentComponent;

							return parentComponent.formatTooltip(
								this.localityCode
							);
						},
					},
				},
			],
		});

		// Override pinch zoom
		HighchartsMapPinchZoom.attach(this.chart);

		// Attach mouse events
		this.chart.container.addEventListener(
			'mousemove',
			this.onMouseMove.bind(this)
		);

		this.chart.container.addEventListener(
			'mousedown',
			this.onMouseDown.bind(this)
		);

		this.chart.container.addEventListener(
			'mouseup',
			this.onMouseUp.bind(this)
		);

		// Add reference to this component
		// from the chart
		this.chart.parentComponent = this;
	}

	initializeTooltip() {
		this.tooltipContainer = this.element.parentElement?.querySelector(
			'.bill-tracker__tooltip-container'
		) as HTMLElement;
		this.tooltipSlot = this.element.parentElement?.querySelector(
			'.bill-tracker__tooltip-slot'
		) as HTMLElement;
		this.tooltipClose = this.element.parentElement?.querySelector(
			'.bill-tracker__tooltip-close'
		) as HTMLButtonElement;
		this.tooltipClose?.addEventListener(
			'click',
			this.closeTooltip.bind(this)
		);

		// Close tooltip on Esc
		document.addEventListener('keyup', ({ key }) => {
			if (/esc/i.test(key)) {
				this.closeTooltip();
			}
		});

		// Move tooltip around, depending on breakpoint
		mobileAndTablet(
			() =>
				this.footer?.insertAdjacentElement(
					'beforebegin',
					this.tooltipContainer as HTMLElement
				),
			() =>
				this.element.insertAdjacentElement(
					'beforeend',
					this.tooltipContainer as HTMLElement
				)
		);
	}

	onChartLoad() {
		this.emit('init', this);

		// Add a slightest delay to ensure chart is initialized
		setTimeout(() => {
			this.chart.update({
				chart: {
					animation: true,
				},
			});
		}, 1);
	}

	onChartRender() {
		setTimeout(() => {
			// Always move zoom buttons to last layer in order to
			// overlap the datalabels (z-index not working in SVG children)
			this.chart.container
				.querySelector('.highcharts-root')
				.appendChild(
					this.chart.container.querySelector('.highcharts-zoom-in')
						.parentElement
				);

			this.emit('render', this);
		}, 1);
	}

	onMouseMove() {
		const { minZoom, zoom } = this.chart.mapView;

		// Check if map is zoomed
		this.element.classList.toggle(
			'bill-tracker__map--zoomed',
			minZoom !== zoom
		);
	}

	onMouseDown() {
		this.element.classList.add('bill-tracker__map--mousedown');
	}

	onMouseUp() {
		this.element.classList.remove('bill-tracker__map--mousedown');
	}

	getStateColorIndex(localityCode: string) {
		const code = localityCode.toUpperCase();
		const stateWithBills = this.statesWithBills?.find(
			(state) => state['hc-key'].toUpperCase() === code
		);
		const billsInViewCount =
			stateWithBills?.bills.filter(this.state.billFilter).length || 0;

		const colorIndex = MIN_COUNT_TO_COLOR_KEYS_REVERSED.find(
			(count) => billsInViewCount >= count || 0
		) as keyof typeof MIN_COUNT_TO_COLOR;

		return colorIndex;
	}

	getStateColor(localityCode: string) {
		const colorIndex = this.getStateColorIndex(localityCode);
		return MIN_COUNT_TO_COLOR[colorIndex];
	}

	showTooltip(localityCode: string) {
		(this.tooltipSlot as HTMLElement).innerHTML =
			this.formatTooltip(localityCode);
		this.tooltipContainer?.classList.remove('hidden');
	}

	closeTooltip() {
		this.tooltipContainer?.classList.add('hidden');
	}

	formatTooltip(localityCode: string) {
		const stateCode = localityCode.toUpperCase();
		const stateWithBills = this.statesWithBills?.find(
			(state) => state['hc-key'].toUpperCase() === stateCode
		);

		if (stateWithBills) {
			const filteredBills = stateWithBills.bills.filter(
				this.state.billFilter.bind(this)
			);

			if (!filteredBills.length) return '';

			return `
			<div class="bill-tracker__tooltip">
				<div class="bill-tracker__tooltip-heading">
					${stateWithBills.name}
				</div>
				<ul class="bill-tracker__tooltip-bills">
						${filteredBills
							.map(
								(bill) => `
							<li class="bill-tracker__tooltip-bill" data-category-group="${bill.category_group}">
								<i class="bill-tracker__tooltip-indicator"></i>
								<a href="${bill.source}" target="_blank"}><span class="bill-tracker__tooltip-bill-id">${bill.primary_external_id}</span> - ${bill.title} (${bill.statusCategory})</a>
							</li>
						`
							)
							.join('')}
				</ul>
			</div>
			`;
		}

		return '';
	}

	updateSeriesItemData(data: SeriesItemData) {
		const colorIndex = this.getStateColorIndex(data.localityCode);
		data.color = MIN_COUNT_TO_COLOR[colorIndex];
		data.borderColor = Number(colorIndex) >= 10 ? '#ccc	' : '#003A48';
		data.dataLabels = {
			color: Number(colorIndex) >= 10 ? 'white' : '#003A48',
			style: {
				textOutline: `1px ${MIN_COUNT_TO_COLOR[colorIndex]}`,
				textDecoration: 'uppercase',
			},
		};

		return data;
	}

	setFilter(filterFunction: (bill: BillData) => boolean = () => true) {
		this.setState({ billFilter: filterFunction });
	}

	export() {
		this.chart.exportChart({
			type: 'pdf',
			filename: 'Bill Tracker Map',
		});
	}

	getBillsInViewTotal() {
		return (
			this.statesWithBills
				?.map(({ bills }) => bills.filter(this.state.billFilter).length)
				.reduce((total, currentCount = 0) => total + currentCount, 0) ||
			0
		);
	}

	handleBreakpoints() {
		// Breakpoint change updates
	}

	componentDidUpdate() {
		const { visible } = this.state;

		if (visible) {
			this.chart?.update(
				{
					series: [
						{
							data: this.seriesData?.map(
								this.updateSeriesItemData.bind(this)
							),
						},
					],
				},
				true,
				false,
				false
			);
		}
	}
}

export default BillTrackerMap;
